import { useState } from 'react';
import { createCheckoutSession } from '../lib/stripe';
import { useToast } from '../context/ToastContext';
import { Loader2 } from 'lucide-react';

interface CheckoutButtonProps {
  priceId: string;
  children: React.ReactNode;
  className?: string;
}

export function CheckoutButton({ priceId, children, className = '' }: CheckoutButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();

  const handleClick = async () => {
    try {
      setIsLoading(true);
      const response = await createCheckoutSession(priceId);
      const data = await response.json();
      
      if (!data.url) {
        throw new Error('No checkout URL received');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      
      let errorMessage = 'An unexpected error occurred during checkout.';
      
      if (error instanceof Error) {
        if (error.message.includes('Network error')) {
          errorMessage = error.message;
        } else if (error.message.includes('No active session')) {
          errorMessage = 'Please log in to continue with the purchase.';
        } else {
          errorMessage = `Checkout failed: ${error.message}`;
        }
      }
      
      showToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={`inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 ${className}`}
    >
      {isLoading ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Processing...
        </>
      ) : (
        children
      )}
    </button>
  );
}